/**
 * Firebase client SDK initialization.
 *
 * The Firebase config is fetched from the API at runtime so each deployment
 * automatically gets the correct project config without build-time injection.
 */
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId: string;
  measurementId?: string;
}

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;

/** Initialize Firebase with the given config. Idempotent. */
export function initFirebase(config: FirebaseConfig): FirebaseApp {
  if (app) return app;
  app = initializeApp(config);
  auth = getAuth(app);
  db = getFirestore(app);
  return app;
}

/** Get the Firebase Auth instance (must call initFirebase first). */
export function getFirebaseAuth(): Auth {
  if (!auth) throw new Error('Firebase not initialized. Call initFirebase() first.');
  return auth;
}

/** Get the Firestore instance (must call initFirebase first). */
export function getFirebaseFirestore(): Firestore {
  if (!db) throw new Error('Firebase not initialized. Call initFirebase() first.');
  return db;
}
